/**  Client-side UserProfile model with custom methods and computed properties */

import type { Address } from '@elara/domain/models/schemas/shared-schemas';
import type { DataOf, InputOf } from '@elara/firebolt/shared';

import type { FirestoreContainer } from '#containers/FirestoreContainer.js';
import type { ClientInstanceOf } from '#firebase/types.js';
import type { AppFlowsContainer } from '#flows.js';
import type { Theme } from '@elara/client-core/ui-contracts/theme';

import {
  DEFAULT_COUNTRY_CODE,
  DEFAULT_CURRENCY,
  DEFAULT_LOCALE,
  DEFAULT_TIMEZONE,
} from '@elara/domain/locale';
import { UserProfileSpec } from '@elara/domain/models/UserProfileSpec';

import { createClientModel } from '#firebase/createClientModel.js';

/**
 * UserProfileCM is a client-side model for user profile data
 * Provides reactive access to user profile data with computed properties
 */
export function createUserProfileModel({
  firestore,
  flows,
}: {
  firestore: FirestoreContainer;
  flows: AppFlowsContainer;
}) {
  const model = createClientModel(
    firestore,
    UserProfileSpec,
    () => ({
      /** Get the display name or fallback to email */
      get displayNameOrEmail(): string {
        return this.displayName || this.email || 'User';
      },
      /** Get initials from display name or email */
      get initials(): string {
        const name = this.displayName || this.email || 'U';
        if (!name) return 'U';
        return name
          .split(' ')
          .map((n) => n.charAt(0))
          .join('')
          .toUpperCase()
          .slice(0, 2);
      },
      /** Check if profile has been fully set up */
      get isProfileComplete(): boolean {
        return !!(this.displayName && this.aboutMe);
      },
      /** Get timezone from attributes or canonical default */
      get timezone(): string {
        return this.attributes?.timezone || DEFAULT_TIMEZONE;
      },
      /** Get locale from attributes or canonical default */
      get locale(): string {
        return this.attributes?.locale || DEFAULT_LOCALE;
      },
      /** Get preferred currency from attributes or canonical default */
      get preferredCurrency(): string {
        return this.attributes?.preferredCurrency || DEFAULT_CURRENCY;
      },
      /** Get country code from address or canonical default */
      get countryCode(): string {
        const address = this.address as Address;
        return address?.countryCode || DEFAULT_COUNTRY_CODE;
      },
      /** Get theme preference from attributes or default to 'system' */
      get theme(): Theme {
        return (this.attributes?.theme as Theme) || 'system';
      },
      /** Get sidebar open preference from attributes or default to true */
      get sidebarOpen(): boolean {
        const v = this.attributes?.sidebarOpen;
        return v === undefined ? true : v === 'true';
      },
    }),
    { update: flows.updateUserProfile, create: flows.createUserProfile },
  );
  return model;
}

export type UserProfileModel = ReturnType<typeof createUserProfileModel>;
export interface UserProfile extends ClientInstanceOf<UserProfileModel> {}
export interface UserProfileData extends DataOf<UserProfileSpec> {}
export interface UserProfileInput extends InputOf<UserProfileSpec> {}
